import { z } from 'zod';
/**
 * Event info sections — the free-form "good to know" an organiser publishes for
 * an event: Wi-Fi, getting there, parking, do's and don'ts, emergency contacts.
 *
 * Duplicated by hand in `backend/src/schemas/event_info.ts` and
 * `backend/src/domain/entities/info_section_entity.ts`, and pinned to those
 * copies — with a behavioural boundary case on EACH side, not just an equality
 * assertion — in `backend/tests/contracts.test.ts`. The backend deliberately
 * does not import this package (see that file's header), so the pin is the only
 * thing holding the two halves together.
 */
/**
 * The icon keys an admin may choose from, in the order the picker renders them.
 *
 * One list, one order, three surfaces: the admin console's grid, the backend's
 * whitelist, and the mobile app's `IconData` map. A key added here without being
 * added to the app's map renders as `info` there rather than crashing, which is
 * the one direction of drift that is safe.
 */
export const INFO_ICONS = [
    'info',
    'wifi',
    'transport',
    'parking',
    'map',
    'hotel',
    'food',
    'schedule',
    'rules',
    'phone',
    'health',
    'badge',
    'tickets',
    'dress',
    'accessibility',
    'help',
];
export const InfoIconSchema = z.enum(INFO_ICONS);
/** Longest a section title may be. */
export const MAX_INFO_TITLE_LENGTH = 80;
/** Longest the one-line teaser shown in lists may be. */
export const MAX_INFO_SUMMARY_LENGTH = 160;
/** Longest the info-markdown body may be. */
export const MAX_INFO_BODY_LENGTH = 20000;
/** Most key-detail rows one section may carry. */
export const MAX_INFO_DETAILS = 20;
/** Longest a key-detail label may be. */
export const MAX_INFO_DETAIL_LABEL_LENGTH = 40;
/** Longest a key-detail value may be. */
export const MAX_INFO_DETAIL_VALUE_LENGTH = 500;
/**
 * One key fact — "Network", "EventGuest" — rendered as a copyable row.
 *
 * `value` is deliberately not a URL, an email or anything else typed: it is
 * whatever the organiser needs a person to be able to copy in one tap.
 */
export const InfoSectionDetailSchema = z.object({
    label: z.string().min(1).max(MAX_INFO_DETAIL_LABEL_LENGTH),
    value: z.string().min(1).max(MAX_INFO_DETAIL_VALUE_LENGTH),
});
/**
 * The most key-detail rows a REQUEST may carry, before blanks are dropped.
 *
 * Deliberately looser than {@link MAX_INFO_DETAILS}, which is the real cap: the
 * console's editor creates an empty label/value row the moment somebody presses
 * "Add", so a payload legitimately arrives with blank rows the server is about to
 * drop. Capping the wire at 20 would refuse twenty real rows plus one stray blank
 * one, and the admin would be told they are over a limit they are not over.
 */
export const MAX_INFO_DETAIL_ROWS_SENT = MAX_INFO_DETAILS * 2;
/**
 * A key-detail row as SENT, which is NOT the same shape as a stored one.
 *
 * `label` and `value` may be empty here. The server drops a row where both are
 * and refuses one where only one is, and this schema deliberately lets both
 * cases through so that rule is the server's to state — a console that refused
 * them first would make the server's own rule unreachable and untestable over
 * HTTP.
 */
export const InfoSectionDetailInputSchema = z.object({
    label: z.string().trim().max(MAX_INFO_DETAIL_LABEL_LENGTH),
    value: z.string().trim().max(MAX_INFO_DETAIL_VALUE_LENGTH),
});
export const InfoSectionDetailsInputSchema = z
    .array(InfoSectionDetailInputSchema)
    .max(MAX_INFO_DETAIL_ROWS_SENT);
/**
 * The wire shape of one section.
 *
 * `icon` is a plain string rather than {@link InfoIconSchema} ON PURPOSE. Every
 * write is whitelisted against `INFO_ICONS` by the server, so the value is one
 * of the sixteen in practice — but a row written before a key was renamed must
 * still be readable, and a response schema that refuses it would take the whole
 * list down rather than one badge. Renderers map an unknown key to `info`.
 *
 * Timestamps are ISO strings: this is what `response.json()` yields.
 */
export const InfoSectionSchema = z.object({
    id: z.string().uuid(),
    eventId: z.string().uuid(),
    title: z.string(),
    summary: z.string().nullable(),
    body: z.string(),
    icon: z.string(),
    details: z.array(InfoSectionDetailSchema),
    published: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * `POST /v1/events/:eventId/info-sections`.
 *
 * SHAPE AND SIZE ONLY. Whether a title is empty, whether a detail row is blank,
 * and how many rows are too many are all the server's decisions, and this schema
 * is written so those refusals are reachable rather than pre-empted — a console
 * that refuses first turns the server's rule into dead code nobody exercises.
 *
 * `.trim()` runs BEFORE `.max()`, so a title padded to exactly the cap means the
 * same thing here as it does on the server. Without it one length would be two
 * different limits on two sides of the same seam.
 */
export const CreateInfoSectionRequestSchema = z.object({
    title: z.string().trim().max(MAX_INFO_TITLE_LENGTH),
    summary: z.string().trim().max(MAX_INFO_SUMMARY_LENGTH).nullable().optional(),
    body: z.string().trim().max(MAX_INFO_BODY_LENGTH).optional(),
    icon: InfoIconSchema.optional(),
    details: InfoSectionDetailsInputSchema.optional(),
    published: z.boolean().optional(),
});
/** `PATCH /v1/info-sections/:id` — every field optional, same bounds. */
export const PatchInfoSectionRequestSchema = z.object({
    title: z.string().trim().max(MAX_INFO_TITLE_LENGTH).optional(),
    summary: z.string().trim().max(MAX_INFO_SUMMARY_LENGTH).nullable().optional(),
    body: z.string().trim().max(MAX_INFO_BODY_LENGTH).optional(),
    icon: InfoIconSchema.optional(),
    details: InfoSectionDetailsInputSchema.optional(),
    published: z.boolean().optional(),
});
/**
 * `PUT /v1/events/:eventId/info-sections/order` — the whole order, not a move.
 *
 * `ids` must be exactly the set of the event's section ids; the server refuses a
 * partial list with `reason: 'order_mismatch'` rather than guessing where the
 * missing ones go. Sending the full list is what makes a reorder idempotent when
 * two admins press the arrows at once: the last write wins outright instead of
 * interleaving two partial moves into an order neither of them chose.
 */
export const ReorderInfoSectionsRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
});
//# sourceMappingURL=event_info.js.map